import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { useLatestBeautyScan } from "./useLatestBeautyScan";
import { ScoreBar, ScanRequiredEmpty, prettyEnum } from "./beautyUi";
import BeautyTrends from "./BeautyTrends";
import { motion } from "framer-motion";
import {
  Sparkles, Camera, Palette, Scissors, Droplet, ShoppingBag,
  History, ArrowRight, Loader2, CalendarClock, FlaskConical,
} from "lucide-react";

const QUICK_ACTIONS = [
  { to: "/beauty/scan",       icon: Camera,        label: "AI Scan",         desc: "Refresh your beauty baseline",         testId: "beauty-quick-scan"    },
  { to: "/beauty/makeup",     icon: Palette,       label: "Makeup Studio",   desc: "Palettes matched to your scan",         testId: "beauty-quick-makeup"  },
  { to: "/beauty/hair-beard", icon: Scissors,      label: "Hair & Beard",    desc: "Styles for your face shape",            testId: "beauty-quick-hair"    },
  { to: "/beauty/care",       icon: Droplet,       label: "Care Point",      desc: "Skin · hair · scalp routine",           testId: "beauty-quick-care"    },
  { to: "/beauty/routine",    icon: CalendarClock, label: "Routine Planner", desc: "Enable reminders in your calendar",     testId: "beauty-quick-routine" },
  { to: "/beauty/ingredients",icon: FlaskConical,  label: "Ingredients",     desc: "Scan any product's actives",            testId: "beauty-quick-ingredients" },
  { to: "/shop?tab=foryou",   icon: ShoppingBag,   label: "Shop For You",    desc: "Products tuned to your concerns",       testId: "beauty-quick-shop"    },
  { to: "/beauty/history",    icon: History,       label: "AI History",      desc: "Revisit and compare past scans",        testId: "beauty-quick-history" },
];

export default function BeautyDashboard() {
  const { user } = useAuth();
  const { scan, loading } = useLatestBeautyScan();
  const analysis = scan?.analysis || {};
  const scores = analysis.overall_scores || {};
  const face = analysis.face || {};
  const beardApplicable = analysis?.beard?.applicable;

  const scanDate = scan?.created_at
    ? new Date(scan.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-dashboard">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Stylebay · Beauty Studio</p>
      <h1 className="editorial-h1 mt-3">
        Hello {user?.name?.split(" ")[0] || "beautiful"} —
        <br /><em className="italic">your beauty brief.</em>
      </h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        One selfie powers Makeup, Hair &amp; Beard, and Care Point. No repeat uploads — every module reads from the same AI Scan.
      </p>

      {/* Snapshot */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-10 grid lg:grid-cols-[1.4fr,1fr] gap-6"
        data-testid="beauty-snapshot"
      >
        <div className="border border-border p-6 lg:p-8">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <p className="eyebrow">Latest AI Scan</p>
              <p className="font-serif text-3xl lg:text-4xl mt-1 leading-tight">
                {scan ? (scanDate || "Just now") : (loading ? "Loading…" : "No scan yet")}
              </p>
            </div>
            <Link
              to="/beauty/scan"
              data-testid="beauty-run-scan-cta"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-4 h-10 text-[11px] uppercase tracking-[0.2em]"
            >
              {scan ? "New Scan" : "Run AI Scan"} <ArrowRight size={13} />
            </Link>
          </div>

          {loading && (
            <div className="mt-8 border border-dashed border-border p-10 text-center">
              <Loader2 size={22} className="animate-spin mx-auto opacity-70" />
            </div>
          )}

          {!loading && !scan && (
            <div className="mt-8">
              <ScanRequiredEmpty />
            </div>
          )}

          {!loading && scan && (
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <ScoreBar label="Skin Health"   value={scores.skin_health}   testId="beauty-score-skin" />
                <ScoreBar label="Hair Health"   value={scores.hair_health}   testId="beauty-score-hair" />
                {beardApplicable && (
                  <ScoreBar label="Beard Health" value={scores.beard_health} testId="beauty-score-beard" />
                )}
              </div>
              <div className="space-y-3 text-sm">
                <Row label="Face shape" value={prettyEnum(face.face_shape)} />
                <Row label="Skin tone" value={prettyEnum(face.skin_tone)} />
                <Row label="Undertone" value={prettyEnum(face.skin_undertone)} />
                <Row label="Priorities" value={(analysis?.skin?.top_priorities || []).slice(0, 3).join(" · ") || "—"} />
              </div>
            </div>
          )}
        </div>

        <div className="border border-border p-6 lg:p-8 bg-card">
          <p className="eyebrow">Today&apos;s reminders</p>
          <p className="font-serif text-2xl mt-2 leading-tight">Small habits, real skin wins.</p>
          <ul className="mt-5 space-y-3 text-sm">
            <ReminderRow text="Morning cleanse + SPF 50" />
            <ReminderRow text="Hydrate before bed" />
            <ReminderRow text="Sunday · deep condition + face mask" />
          </ul>
          <Link
            to="/beauty/care"
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] underline underline-offset-4"
            data-testid="beauty-plan-routine-cta"
          >
            Plan my routine <ArrowRight size={13} />
          </Link>
        </div>
      </motion.section>

      {/* Trends chart (only visible after ≥2 scans) */}
      <BeautyTrends />

      {/* Quick actions */}
      <section className="mt-14" data-testid="beauty-quick-actions">
        <p className="eyebrow">Jump in</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {QUICK_ACTIONS.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              data-testid={a.testId}
              className="group relative border border-border p-6 hover:border-foreground transition-all duration-300 hover:-translate-y-1"
            >
              <div className="h-11 w-11 inline-flex items-center justify-center bg-muted group-hover:bg-accent group-hover:text-accent-foreground transition-colors mb-5">
                <a.icon size={20} strokeWidth={1.4} />
              </div>
              <p className="font-serif text-2xl leading-tight">{a.label}</p>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border pb-2">
      <p className="eyebrow">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

function ReminderRow({ text }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-1.5 h-1.5 bg-accent mt-2" />
      <span>{text}</span>
    </li>
  );
}
